// Node related configs
// Keep any constant related to the node here

import type { Node, NodeTypes } from '@xyflow/react'
import { v4 } from 'uuid'
import { HOLD_MUSIC_URL } from 'v2/configs/constants'
import { type FlowTypes, TriggerType } from 'v2/types'
import { DEFAULT_INITIAL_WAIT_TIME } from '../constants'
import { GenericNode } from '../nodes/generic-node'
import { ComponentMenu } from '../nodes/new-node-menu'
import { NoteNode } from '../nodes/note'
import { SelectTrigger } from '../nodes/select-trigger'
import { Start } from '../nodes/start'
import type { StreamNodeData } from '../types'
import {
  type AgentNodeNodeData,
  type AgentPresetConversationNodeData,
  type AiActionNodeData,
  type AiAgentCallNodeData,
  type AiAgentChatNodeData,
  type AiAgentMessageNodeData,
  type AiAgentNodeformData,
  type AiAgentV2NodeData,
  type AiAgentWhatsappNodeData,
  type AiAssistNodeData,
  type AiSkillsNodeData,
  type AppActionNodeData,
  type BranchV2NodeData,
  type BusinessHourNodeData,
  type CallForwardNodeData,
  type ConferenceBridgeNodeData,
  type ContactScreeningNodeData,
  type CounterNodeData,
  type CreateTaskNodeData,
  type CustomerFeedbackNodeData,
  type EndConversationNodeData,
  FlowNodeTypes,
  type GetInputNodeData,
  type HangupNodeData,
  type HttpRequestNodeData,
  type InitiateCallNodeData,
  type IVRNodeData,
  type MessageConversationNodeData,
  type MultiPartyCallOutboundNodeData,
  type MultiPartyCallV2NodeData,
  type NodeDataBase,
  type NoteNodeData,
  type OutboundScreeningNodeData,
  type OutputStates,
  type PromptNodeData,
  type QueueAndRouteNodeData,
  type SendChatNodeData,
  type SendMessageNodeData,
  type SendWhatsappNodeData,
  type StartNodeData,
  type UserInputNodeData,
  type VoicemailNodeData,
  type WhatsappConversationNodeData,
} from '../types'
import { getTriggerTypeFromPhloType } from '../utils'
import { AI_AGENT_V29_LABEL } from '../utils/ai-agent-v29'
import { SCREENING_OUTCOME_PORTS } from './contact-screening.config'
import { OUTBOUND_SCREENING_CARRIER_PORTS } from './outbound-screening.config'

export const getTempNodeData = (name: string) => ({
  meta: {
    name,
  },
  config: {
    name,
  },
})

export function getDefaultStartBaseNode(options?: {
  triggerNode?: Node<NodeDataBase<unknown>>
  phloType?: FlowTypes
}): Node<StartNodeData> {
  const baseId = v4()
  const basePosition = { x: 250, y: 250 }

  const baseDataConfig = {
    name: 'Start',
    description: '',
    require_http_auth: true,
    enable_quick_responses: false,
    triggers: [TriggerType.call],
  }

  let id = baseId
  let position = basePosition
  // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
  let config: any = { ...baseDataConfig }

  if (options?.triggerNode) {
    id = options.triggerNode.id
    position = options.triggerNode.position
  }

  if (options?.phloType) {
    config = {
      ...config,
      triggers: [getTriggerTypeFromPhloType(options.phloType)],
    }
  }

  return {
    id,
    type: FlowNodeTypes.start,
    data: {
      meta: {
        name: 'Start',
      },
      config,
    },
    position,
  }
}
// Default start node - Start node complete with default data
export const defaultStartNode: Node<StartNodeData> = (() => {
  const baseNode = getDefaultStartBaseNode()
  return {
    ...baseNode,
    data: {
      ...baseNode.data,
      config: {
        ...baseNode.data.config,
        url: '',
        auth_token: '****',
        auth_id: '',
        payload_format: '{}',
        quick_response_questions: [],
        events: [],
      },
    },
  }
})()

// Output states for each component
const digitsAndSymbols = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '#', '0', '*'] // Predefined array

// Common output states for AI agent components
export const aiAgentGeneratedOutputStates = [
  {
    default: true,
    id: 'human_handoff',
    name: 'Transfer to human',
    meta: { conversation_type: ['ai_generated'] },
  },
  {
    default: true,
    id: 'conversation_complete',
    name: 'Conversation Complete',
    meta: { conversation_type: ['ai_generated'] },
  },
]

export const aiAgentPresetMessageOutputStates = [
  {
    default: true,
    id: 'sent',
    name: 'Sent',
    meta: { conversation_type: ['preset_message'], wait: false },
  },
  {
    default: true,
    id: 'reply',
    name: 'Reply',
    meta: { conversation_type: ['preset_message'], wait: true },
  },
  {
    default: true,
    id: 'no reply',
    name: 'No Reply',
    meta: { conversation_type: ['preset_message'], wait: true },
  },
]

export const aiAgentInequippedToServeOutputState = {
  default: true,
  id: 'inequipped_to_serve',
  name: 'Inequipped to Serve',
  meta: { conversation_type: ['ai_generated'] },
}

export const aiAgentErrorOutputState = {
  default: true,
  id: 'error',
  name: 'Error',
  meta: { conversation_type: ['ai_generated', 'preset_message'], wait: false },
}

export const aiAgentAllOutputStates = [
  ...aiAgentGeneratedOutputStates,
  ...aiAgentPresetMessageOutputStates,
  aiAgentInequippedToServeOutputState,
  aiAgentErrorOutputState,
]

export const outputStates: OutputStates = {
  [FlowNodeTypes.start]: [],
  [FlowNodeTypes.menu]: [],
  [FlowNodeTypes.note]: [],
  [FlowNodeTypes.select_trigger]: [],
  [FlowNodeTypes.create_task]: [
    { default: true, id: 'Success', name: 'Success' },
    { default: true, id: 'Failed', name: 'Failed' },
  ],
  // Add more output states below
  [FlowNodeTypes.multi_party_call_v2]: [
    { default: true, id: 'completed', name: 'Completed' },
    { default: true, id: 'no-answered', name: 'No Answer' },
    { default: true, id: 'busy-rejected', name: 'Busy/Rejected' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.conference_bridge]: [
    { default: true, id: 'completed', name: 'Completed' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.agent_preset_conversation]: [
    { default: true, id: 'sent', name: 'Sent', meta: { wait: false } },
    { default: true, id: 'reply', name: 'Reply', meta: { wait: true } },
    { default: true, id: 'no reply', name: 'No Reply', meta: { wait: true } },
    { default: true, id: 'failed', name: 'Failed', meta: { wait: false } },
  ],
  [FlowNodeTypes.ai_skills]: [
    { default: true, id: 'success', name: 'Success' },
    { default: true, id: 'error', name: 'Error' },
  ],

  [FlowNodeTypes.end_conversation]: [],
  [FlowNodeTypes.ai_agent_v2]: [aiAgentErrorOutputState],
  [FlowNodeTypes.ai_agent_v29]: [aiAgentErrorOutputState],
  [FlowNodeTypes.agent_node]: [aiAgentErrorOutputState],
  // Fixed ports; ids double as wire intent names and edge keys — see
  // config/contact-screening.config.ts.
  [FlowNodeTypes.contact_screening]: [...SCREENING_OUTCOME_PORTS],
  // Composite: carrier outcomes + screening outcomes ('answered' is internal).
  [FlowNodeTypes.outbound_screening]: [
    ...OUTBOUND_SCREENING_CARRIER_PORTS,
    ...SCREENING_OUTCOME_PORTS,
  ],
  [FlowNodeTypes.ai_action]: [aiAgentErrorOutputState],
  // Stream node: Audio mode only has completed/error states (no custom intents)
  // Text mode supports custom intents which are added via edge connections
  [FlowNodeTypes.stream]: [
    { default: true, id: 'completed', name: 'Completed' },
    { default: true, id: 'error', name: 'Error' },
  ],

  [FlowNodeTypes.initiate_call]: [
    { default: true, id: 'answered', name: 'Answered' },
    { default: true, id: 'no_answer', name: 'No Answer' },
    { default: true, id: 'busy_rejected', name: 'Busy/Rejected' },
    { default: true, id: 'failed', name: 'Failed' },
    { default: false, id: 'voicemail', name: 'Voicemail' },
  ],

  [FlowNodeTypes.ai_assist]: [
    { default: true, id: 'agent_assist', name: 'Agent Assist' },
    { default: true, id: 'resolved', name: 'Resolved' },
    { default: true, id: 'ai_error', name: 'Error' },
  ],

  [FlowNodeTypes.user_input]: [
    { default: true, id: 'success', name: 'Success' },
    { default: true, id: 'skip', name: 'Skip' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.ai_agent_call]: [...aiAgentGeneratedOutputStates, aiAgentErrorOutputState],

  [FlowNodeTypes.ai_agent_message]: aiAgentAllOutputStates,

  [FlowNodeTypes.ai_agent_chat]: aiAgentAllOutputStates,

  [FlowNodeTypes.ai_agent_whatsapp]: aiAgentAllOutputStates,
  [FlowNodeTypes.branch_v2]: [
    { default: true, id: 'no_match', name: 'No Match' },
    { default: true, id: 'error', name: 'Error' },
  ],
  [FlowNodeTypes.send_whatsapp]: [
    { default: true, id: 'sent', name: 'Sent' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.send_chat]: [
    { default: true, id: 'sent', name: 'Sent' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.send_message]: [
    { default: true, id: 'sent', name: 'Sent', meta: { wait: false } },
    { default: true, id: 'reply', name: 'Reply', meta: { wait: true } },
    { default: true, id: 'no reply', name: 'No Reply', meta: { wait: true } },
    { default: true, id: 'failed', name: 'Failed', meta: { wait: false } }, // Failed state is used for both wait and no wait
  ],
  [FlowNodeTypes.multi_party_call_outbound]: [
    { default: true, id: 'completed', name: 'Completed' },
    { default: true, id: 'no-answered', name: 'No Answer' },
    { default: true, id: 'busy-rejected', name: 'Busy/Rejected' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.whatsapp_conversation]: [
    { default: true, id: 'completed', name: 'Completed' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.message_conversation]: [
    { default: true, id: 'completed', name: 'Completed' },
    { default: true, id: 'failed', name: 'Failed' },
  ],

  [FlowNodeTypes.business_hour]: [{ default: true, id: 'no_match', name: 'No Match' }],

  [FlowNodeTypes.http_request]: [
    { default: true, id: 'success', name: 'Success' },
    { default: true, id: 'failed', name: 'Failed' },
  ],
  [FlowNodeTypes.counter]: [
    { default: true, id: 'count_limit_reached', name: 'Count Reached' },
    { default: true, id: 'count_limit_not_reached', name: 'Count not Reached' },
  ],
  [FlowNodeTypes.queue_and_route]: [
    { default: true, id: 'completed', name: 'Completed conversation' },
    { default: true, id: 'enqueue_failed', name: 'Enqueue Failed' },
    { default: true, id: 'timeout', name: 'Timeout' },
    { default: true, id: 'assignment_failed', name: 'Assignment Failed' },
  ],
  [FlowNodeTypes.customer_feedback]: [
    { default: true, id: '1', name: '1 Rating' },
    { default: true, id: '2', name: '2 Rating' },
    { default: true, id: '3', name: '3 Rating' },
    { default: true, id: '4', name: '4 Rating' },
    { default: true, id: '5', name: '5 Rating' },
    { default: true, id: 'wrong_input', name: 'Wrong Input' },
    { default: true, id: 'no_input', name: 'No Input' },
  ],
  [FlowNodeTypes.call_forward]: [
    { default: true, id: 'completed', name: 'Call answered' },
    { id: 'no_answer', name: 'No Answer', default: true },
    { default: true, id: 'busy_rejected', name: 'Busy/Rejected' },
    { default: true, id: 'failed', name: 'Failed' },
  ],
  [FlowNodeTypes.get_input]: [
    { default: true, id: 'input_success', name: 'Successful Response' },
    { default: true, id: 'no_input', name: 'No response' },
  ],
  [FlowNodeTypes.voicemail]: [
    { default: true, id: 'recording_completed', name: 'Recording Completed' },
  ],
  [FlowNodeTypes.prompt]: [{ default: true, id: 'prompt_completed', name: 'Audio Completed' }],
  [FlowNodeTypes.hangup]: [],
  [FlowNodeTypes.ivr_menu]: [
    { default: true, id: 'no_input', name: 'No Input' },
    { default: true, id: 'wrong_input', name: 'Wrong Input' },
    ...digitsAndSymbols.map((digit) => ({
      default: false,
      id: digit,
      name: `Customer pressed ${digit}`,
    })),
  ],
  [FlowNodeTypes.app_action]: [],
}

export const holdMusicData = {
  file: 'music.mp3',
  prompt_type: 'play',
  url: HOLD_MUSIC_URL,
}
// Default data for audio prompts
export const promptData = {
  delay: 0,
  file: '',
  language: 'en-US',
  prompt_speak_type: 'basic',
  prompt_type: 'speak',
  loops: 0,
  silence: 0,
  beep: false,
  text: '',
  url: '',
  voice: 'WOMAN',
}

export const defaultConditions = (index: number) => ({
  id: v4(),
  alias: `Condition ${index + 1}`,
  expression_operators: ['and'],
  expressions: [
    {
      case_sensitive: false,
      lhs: '',
      operation: '',
      rhs: [],
    },
  ],
})

// Default node data for each component - add below
const multiPartyCallV2NodeData: MultiPartyCallV2NodeData = {
  meta: {
    name: 'Multi-party call',
  },
  config: {
    name: 'Multi-party call',
    from: '',
    to: [],
    role: 'agent',
    call_type: 'agent_dials',
    record: false,
    record_mode: 'always',
    record_condition: '',
    recording_option: 'all_together',
    transfer: 'warm',
    // The BE speak_or_play rule rejects a play prompt whose url has no file
    // name ("Please enter file name") — an empty-file default made every fresh
    // MPC node unpublishable. SER-5919.
    customer_hold_music: {
      url: HOLD_MUSIC_URL,
      file: 'music.mp3',
    },
    agent_hold_music: {
      url: HOLD_MUSIC_URL,
      file: 'music.mp3',
    },
    voicemail_message: promptData,
    extra_headers: '',
    events: [],
  },
}

const conferenceBridgeNodeData: ConferenceBridgeNodeData = {
  meta: {
    name: 'Conference',
  },
  config: {
    name: 'Conference',
    conference_name: '',
    conference_type: 'custom',
    participant_pin: '',
    mod_pin: '',
    pin_entry_tries: 3,
    record: false,
    record_mode: 'always',
    record_condition: '',
    announcement: promptData,
    hold_prompt: {
      url: HOLD_MUSIC_URL,
      file: '',
    },
    entry_chime: 'none',
    exit_chime: 'none',
    join_muted: false,
    hold_callers: false,
    events: [],
  },
}

const streamNodeData: StreamNodeData = {
  meta: {
    name: 'Stream',
  },
  config: {
    name: 'Stream',
    mode: 'audio',
    websocket_endpoint: '',
    // Audio mode options
    enable_noise_cancellation: false,
    noise_cancellation_level: 85,
    enable_recording: false,
    // Text mode options
    play_greeting: false,
    first_message: '',
    stt_config: {
      provider: '',
      model: '',
      language: '',
      turn_detection: true,
      live_transcription: false,
      end_of_turn_threshold: 0.5,
      end_of_turn_timeout: 1000,
      keyterms: '',
    },
    tts_config: {
      provider: '',
      model: '',
      language: '',
      gender: undefined,
      voice_id: '',
      speed: 1.0,
      stability: 0.5,
      filler_audio: 'none',
    },
    background_audio: {
      type: 'none',
      volume: 1,
      thinking_sound_enabled: true,
      thinking_sound_volume: 0.7,
    },
  },
}

const agentPresetConversationNodeData: AgentPresetConversationNodeData = {
  meta: {
    name: 'Send message',
  },
  config: {
    name: 'Send message',
    channel: 'message',
    conversation_type: 'preset_message',
    wait_for_response: false,
    reply_timeout: 3600,

    // Message channel specific fields
    from: '',
    to: '',
    text: '',
    message_media: [],
    events: [],

    // WhatsApp channel specific fields
    whatsapp_media: [],
    whatsapp_msg_type: 'text',
    header_type: 'none',
    header_text: '',
    header_media: undefined,
    body: '',
    footer: '',
    whatsapp_list: {
      header: '',
      body: '',
      footer: '',
      button: '',
    },
    sections: [],
    buttons: [],
    variables: {
      header: [],
      body: [],
    },
    template: {},

    // Chat channel specific fields
    content_type: 'text',
    chat_media: {},
    title: '',
    button_description: '',
  },
}

const aiSkillsNodeData: AiSkillsNodeData = {
  meta: {
    name: 'Skills',
  },
  config: {
    name: 'Skills',
    tool_type: '',
  },
}

export const endConversationNodeData: EndConversationNodeData = {
  meta: {
    name: 'End conversation',
  },
  config: {
    name: 'End conversation',
    description: '',
    end_message: '',
  },
}
const DEFAULT_PYTHON_CODE = `# There should always be a function called "main" with typed parameters
def main(a: float, b: float) -> float:
    """
    Calculate the sum of two numbers.

    Parameters:
    - a (float): The first number.  
    - b (float): The second number.

    Returns:
    - float: The sum of the two numbers.
    """
    return a + b`

const aiActionNodeData: AiActionNodeData = {
  meta: {
    name: 'AI actions',
  },
  config: {
    name: 'AI actions',
    actions: [
      {
        id: v4(),
        action_type: 'EXECUTE_ACTION',
        is_expanded: true,
        action_name: null,
        variable_name: null,
        variable_instructions: null,
        action_instructions: null,
        org_app_uuid: null,
        app_provider: null,
        code_name: null,
        code_description: null,
        code_body: DEFAULT_PYTHON_CODE,
        code_function_schema: null,
        name: 'Action 1',
        action_inputs: {},
      },
    ],
    intents: [],
  },
}

const initiateCallNodeData: InitiateCallNodeData = {
  meta: {
    name: 'Initiate call',
  },
  config: {
    name: 'Initiate call',
    to: '',
    ring_timeout: 30,
    detect_voicemail: false,
    send_digits: '',
    from: '',
    is_validate_caller_id: true,
    extra_headers: '',
    max_call_duration: 10, // 10 minutes -> 600 seconds
    events: [],
  },
}

const noteNodeData: NoteNodeData = {
  meta: {
    name: 'Note',
  },
  config: {
    name: 'Note',
    content: '',
    color: 'blue',
  },
}

const aiAssistNodeData: AiAssistNodeData = {
  meta: {
    name: 'AI assist',
  },
  config: {
    name: 'AI assist',
  },
}

const userInputNodeData: UserInputNodeData = {
  meta: {
    name: 'Collect data',
  },
  config: {
    name: 'Collect data',
    contact_identifier: false,
    input_validation: 'any',
    optional: true,
    placeholder_text: 'Reply',
    prompt: '',
    reply_timeout: 3600,
  },
}

const branchV2NodeData: BranchV2NodeData = {
  meta: {
    name: 'Rule branching',
  },
  config: {
    name: 'Rule branching',
    conditions: [defaultConditions(0)],
  },
}
export const messageMediaData = {
  media_type: 'image/jpeg',
  name: '',
  url: '',
  size: 0,
}

// Removed MOCK_APP_FIELDS as we now use dynamic action_inputs

export const actionInitialState: (index: number) => AiAgentNodeformData['actions'][number] = (
  index,
) => ({
  id: v4(),
  action_type: 'EXECUTE_ACTION',
  is_expanded: true,
  action_name: null,
  variable_name: null,
  variable_instructions: null,
  action_instructions: null,
  org_app_uuid: null,
  app_provider: null,
  code_name: null,
  code_description: null,
  code_body: DEFAULT_PYTHON_CODE,
  code_function_schema: null,
  name: `Action ${index + 1}`,
  action_inputs: {},
})

export const aiAgentV2ActionInitialState = (index: number) => ({
  id: v4(),
  action_type: 'EXECUTE_ACTION' as const,
  is_expanded: true,
  action_name: null,
  action_instructions: null,
  action_schema: null,
  action_inputs: {},
  action_field_types: { required_fields: {}, optional_fields: {} },
  org_app_uuid: null,
  app_provider: null,
  code_name: null,
  code_description: null,
  code_body: DEFAULT_PYTHON_CODE,
  code_function_schema: null,
  name: `Action ${index + 1}`,
  // HTTP action fields
  http_tool_name: null,
  http_tool_description: null,
  http_method: 'post',
  http_url: null,
  http_auth_type: 'no_auth',
  http_auth_username: null,
  http_auth_password: null,
  http_auth_token: null,
  http_headers: '{}',
  http_params: '{}',
  http_body_type: 'none',
  http_body_template: null,
  http_function_schema: null,
  http_timeout_ms: 1000,
  http_retry_limit: 0,
})

const INTENT_DEFAULTS = {
  ai_agent: { name: 'User responded', instructions: 'Proceeds when the user responds.' },
  ai_action: {
    name: 'Action executed',
    instructions: 'Take this path when the action is executed.',
  },
  stream: {
    name: 'Stream complete',
    instructions: 'Proceeds when the stream completes successfully.',
  },
} as const

type IntentType = keyof typeof INTENT_DEFAULTS

const createIntentInitialState = (type: IntentType) => () => ({
  id: v4(),
  intent_name: INTENT_DEFAULTS[type].name,
  intent_instructions: INTENT_DEFAULTS[type].instructions,
})

export const intentInitialState = createIntentInitialState('ai_agent')
export const actionIntentInitialState = createIntentInitialState('ai_action')
export const streamIntentInitialState = createIntentInitialState('stream')

// Base configuration for AI Agent nodes
export const baseAiAgentConfig = {
  model_config: {
    model: '',
    provider: '',
  },
  disable_message_type: false,
  disable_inequipped_to_serve: false,
  reply_timeout: 3600,
  disable_human_handoff: false,
  instruction_attachments: [],
  message_type: 'GENERATED' as 'GENERATED' | 'STATIC' | 'NONE',
  message_prompt: '',
  message_text: '',
  instructions: '',
  no_response_timeout: 3600,
  completion_criteria: '',
  no_response_instructions:
    "Send a polite follow-up message reminding the customer you're available to assist",
  return_to_source: false,
  actions: [],
  node_config: {
    intents: [
      {
        id: v4(),
        intent_name: '',
        intent_instructions: '',
      },
    ],
  },
  human_handoff_instructions:
    'If the user asks for a human agent, notify the customer that a support representative will follow up.',
  screening_name: '',
  screening_reason: '',
}

// Default node data for each component - add below
export const aiAgentCallNodeData: AiAgentCallNodeData = {
  meta: {
    name: 'Call conversation',
  },
  config: {
    name: 'Call conversation',
    channel: 'call',
    ...baseAiAgentConfig,
    conversation_type: 'ai_generated',
    wait_for_response: true,
    human_handoff_instructions:
      'If a query requires human intervention, notify the customer that a support representative will follow up and provide a timeframe for resolution.',
  },
}

const aiAgentMessageNodeData: AiAgentMessageNodeData = {
  meta: {
    name: 'SMS conversation',
  },
  config: {
    name: 'SMS conversation',
    channel: 'message',
    ...baseAiAgentConfig,
    conversation_type: 'ai_generated',
    wait_for_response: true,
    human_handoff_instructions:
      'If the user asks for a human agent, notify the customer that a support representative will follow up.',
    from: '',
    to: '',
    text: '',
    message_media: [],
  },
}
const sendWhatsappNodeData: SendWhatsappNodeData = {
  meta: {
    name: 'WhatsApp',
  },
  config: {
    name: 'WhatsApp',
    from: '',
    to: '',
    text: '',
    whatsapp_media: [],
    whatsapp_msg_type: 'custom',
    template: {},
    whatsapp_template: {},
    variables: {
      header: [],
      body: [],
    },
  },
}

const aiAgentChatNodeData: AiAgentChatNodeData = {
  meta: {
    name: 'Chat conversation',
  },
  config: {
    name: 'Chat conversation',
    channel: 'chat',
    ...baseAiAgentConfig,
    conversation_type: 'ai_generated',
    wait_for_response: true,
    human_handoff_instructions:
      'If the user asks for a human agent, notify the customer that a support representative will follow up.',
    content_type: 'text',
    text: '',
    chat_media: {},
    button_description: '',
  },
}

const aiAgentWhatsappNodeData: AiAgentWhatsappNodeData = {
  meta: {
    name: 'WhatsApp conversation',
  },
  config: {
    name: 'WhatsApp conversation',
    channel: 'whatsapp',
    ...baseAiAgentConfig,
    conversation_type: 'ai_generated',
    wait_for_response: true,
    human_handoff_instructions:
      'If the user asks for a human agent, notify the customer that a support representative will follow up.',
    from: '',
    to: '',
    whatsapp_media: [],
    whatsapp_msg_type: 'text',
    variables: {
      header: [],
      body: [],
    },
    buttons: [],
    whatsapp_list: {
      button: '',
    },
    header_type: 'none',
    header_text: '',
    body: '',
    footer: '',
  },
}
const sendChatNodeData: SendChatNodeData = {
  meta: {
    name: 'Chat',
  },
  config: {
    name: 'Chat',
    chat_content_type: 'text',
    text: '',
    chat_media: {},
  },
}

const aiAgentV2NodeData: AiAgentV2NodeData = {
  meta: {
    name: 'AI conversation',
  },
  // @ts-expect-error - Since the component will get the default channel dynamically
  // from the flowStore agentMeta therefore no need to pass channel here
  config: {
    name: 'AI conversation',
    ...baseAiAgentConfig,
    // Remove reply_timeout from base config - it's only applicable for non-call channels
    // and will be added by the sidebar form when needed
    reply_timeout: undefined,
    extract_variables: [],
    actions: [],
    intents: [],
    initial_wait_time: DEFAULT_INITIAL_WAIT_TIME,
  },
}

// Same node as ai_agent_v2; only the name and the wire type differ.
const aiAgentV29NodeData: AiAgentV2NodeData = {
  meta: { name: AI_AGENT_V29_LABEL },
  config: {
    ...aiAgentV2NodeData.config,
    name: AI_AGENT_V29_LABEL,
    channel: 'call', // voice-only: never takes the agent's default channel
  },
}

const agentNodeNodeData: AgentNodeNodeData = {
  meta: {
    name: 'Collect input',
  },
  config: {
    name: 'Collect input',
    channel: 'call',
    instructions: '',
    agent_tasks: { variables: [], extract_only: [] },
    actions: [],
    intents: [],
    initial_wait_time: DEFAULT_INITIAL_WAIT_TIME,
  },
}

const contactScreeningNodeData: ContactScreeningNodeData = {
  meta: {
    name: 'Screening Contact',
  },
  config: {
    name: 'Screening Contact',
    context: '',
    ask_permission: true,
    initial_wait_time: DEFAULT_INITIAL_WAIT_TIME,
  },
}

const outboundScreeningNodeData: OutboundScreeningNodeData = {
  meta: {
    name: 'Initiate call and screen',
  },
  config: {
    name: 'Initiate call and screen',
    from: '',
    to: '',
    is_validate_caller_id: true,
    ring_timeout: 30,
    max_call_duration: 10,
    context: '',
    ask_permission: true,
    initial_wait_time: DEFAULT_INITIAL_WAIT_TIME,
    events: [],
  },
}

const sendMessageNodeData: SendMessageNodeData = {
  meta: {
    name: 'SMS Conversation',
  },
  config: {
    name: 'SMS Conversation',
    from: '',
    to: '',
    text: '',
    message_media: [],
    wait_for_response: false,
    reply_timeout: 3600,
    events: [],
  },
}
const multiPartyCallOutboundNodeData: MultiPartyCallOutboundNodeData = {
  meta: {
    name: 'Outbound call',
  },
  config: {
    name: 'Outbound call',
    agent_hold_music: holdMusicData,
    customer_hold_music: holdMusicData,
  },
}

const whatsappConversationNodeData: WhatsappConversationNodeData = {
  meta: {
    name: 'Outbound whatsapp',
  },
  config: {
    name: 'Outbound whatsapp',
  },
}

const messageConversationNodeData: MessageConversationNodeData = {
  meta: {
    name: 'Outbound SMS',
  },
  config: {
    name: 'Outbound SMS',
  },
}

const businessHourNodeData: BusinessHourNodeData = {
  meta: {
    name: 'Business hour',
  },
  config: {
    name: 'Business hour',
    business_hrs: [],
    output_states: [],
  },
}

const appActionNodeData: AppActionNodeData = {
  meta: {
    name: 'App action',
  },
  config: {
    name: 'App action',
    actions: '',
  },
}

export const initialBody = '{"none":{},"form_data":{},"json":{},"form_urlencoded":{}}'
const httpRequestNodeData: HttpRequestNodeData = {
  meta: {
    name: 'HTTP request',
  },
  config: {
    name: 'HTTP request',
    http_request: {
      name: '',
      auth_params: {
        username: '',
        password: '',
        token: '',
      },
      auth_type: 'no_auth',
      body: initialBody,
      body_type: 'none',
      headers: '{}',
      method: 'get',
      oauth2_details: {
        uuid: '',
      },
      params: '{}',
      response_format: '{}',
      response_type: 'text/html',
      retry_limit: 0,
      settings: null,
      timeout_ms: 1000, // Max timeout is of 5 second
      url: '', // Default URL
    },
  },
}

const createTaskNodeData: CreateTaskNodeData = {
  meta: {
    name: 'Create task',
  },
  config: {
    name: 'Create task',
    message: '',
    create_task_queue_type: 'last_in_queue',
    create_task_type: 'Follow_up',
    channel: 'call',
    due_date: {
      due_date_time_value: 1,
      due_date_time_unit: 'hours',
    },
    priority: 'Medium',

    prompt: promptData,
  },
}

const counterNodeData: CounterNodeData = {
  meta: {
    name: 'Counter',
  },
  config: {
    name: 'Counter',
    max_count: 0,
  },
}

export const queueAndRouteNodeData: QueueAndRouteNodeData = {
  meta: {
    name: 'Assign to human',
  },
  config: {
    name: 'Assign to human',
    queue: '',
    retry_limit: 0,
    sticky_routing_options: 'Loosely bind',
    selectSource: 'existing',
    routing_teams: [],
    routing_agents: [],
    routing_variables: [],
    routing_skills_filter: [],
    routing: 'Availability',
    agent_limit: 3,
    expiry_time_value: 1,
    expiry_time_unit: 'days',
    expiry: false,
    auto_accept_call: {
      allow_auto_accept: false,
      auto_accept_delay: 1,
    },
    queue_call_allowed_inputs: [],
    context: { enabled: false, sources: ['external'], fields: [] },
  },
}
const customerFeedbackNodeData: CustomerFeedbackNodeData = {
  meta: {
    name: 'Customer feedback',
  },
  config: {
    name: 'Customer feedback',
    prompt: {
      ...promptData,
      text: 'Please take a moment to rate your satisfaction with our service.',
    },
    input_timeout: 5,
    delay: 0,
    interrupt: true,
    message: 'Please take a moment to rate your satisfaction with our service.',
  },
}

const callForwardNodeData: CallForwardNodeData = {
  meta: {
    name: 'Call forward',
  },
  config: {
    name: 'Call forward',
    from: '',
    to: '',
    is_validate_caller_id: true,
    extra_headers: '',
    ring_timeout: 20,
    confirm_sound: false,
    confirm_key: '#',
    confirm_prompt: { text: '' },
    events: [],
  },
}

const getInputNodeData: GetInputNodeData = {
  meta: {
    name: 'Collect response',
  },
  config: {
    name: 'Collect response',
    prompt: [promptData],
    input_timeout: 5,
    max_input_size: 99,
    interrupt: true,
    events: [],
  },
}

const voicemailNodeData: VoicemailNodeData = {
  meta: {
    name: 'Record voicemail',
  },
  config: {
    name: 'Record voicemail',
    play_beep: true,
    transcription_type: 'none',
    transcription_url: '',
    voicemail_timeout: 5, // end recording on silence
    voicemail_length: 300,
    end_recording: '*',
    prompt: promptData,
  },
}

export const promptNodeData: PromptNodeData = {
  meta: {
    name: 'Play audio',
  },
  config: {
    name: 'Play audio',
    prompt: [promptData],
  },
}

// IVR Node default data
export const defaultIVRNodeData: IVRNodeData = {
  meta: {
    name: 'IVR menu',
  },
  config: {
    name: 'IVR menu',
    allowed_inputs: [],
    input_timeout: 5,
    interrupt: true,
    digit_timeout: 5,
    num_digits: 1,
    prompt: [promptData],
    events: [],
  },
}

const hangupNodeData: HangupNodeData = {
  meta: {
    name: 'Hangup',
  },
  config: {
    name: 'Hangup',
    reason: 'rejected', // to support null initially
  },
}

// Various node types in our flow
// Add new node types here first
// Follow the same naming pattern - [FlowNodeTypes.component_type]: NewComponent^Node
// Notice the "Node" suffix in the component name
// Special nodes with unique UI/logic
const specialNodeTypes = {
  [FlowNodeTypes.start]: Start,
  [FlowNodeTypes.menu]: ComponentMenu,
  [FlowNodeTypes.select_trigger]: SelectTrigger,
  [FlowNodeTypes.note]: NoteNode,
  // Add more nodes below here
}

// Automatically derive generic node types (all FlowNodeTypes except special ones)
const genericNodeTypes = Object.values(FlowNodeTypes).filter(
  (type) => !Object.keys(specialNodeTypes).includes(type),
)

export const nodeTypes: NodeTypes = {
  ...specialNodeTypes,
  ...Object.fromEntries(genericNodeTypes.map((type) => [type, GenericNode])),
}

// Various default data types in our flow
// Follow the same naming pattern - [FlowNodeTypes.component_type]: Component^NodeData
// Notice the "NodeData" suffix in the Type name
export const defaultNodeData: Record<FlowNodeTypes, NodeDataBase<unknown>> = {
  [FlowNodeTypes.start]: defaultStartNode.data,
  [FlowNodeTypes.multi_party_call_v2]: multiPartyCallV2NodeData,
  [FlowNodeTypes.conference_bridge]: conferenceBridgeNodeData,
  [FlowNodeTypes.stream]: streamNodeData,

  [FlowNodeTypes.agent_preset_conversation]: agentPresetConversationNodeData,
  [FlowNodeTypes.ai_skills]: aiSkillsNodeData,

  [FlowNodeTypes.end_conversation]: endConversationNodeData,
  [FlowNodeTypes.ai_agent_v2]: aiAgentV2NodeData,
  [FlowNodeTypes.ai_agent_v29]: aiAgentV29NodeData,
  [FlowNodeTypes.agent_node]: agentNodeNodeData,
  [FlowNodeTypes.contact_screening]: contactScreeningNodeData,
  [FlowNodeTypes.outbound_screening]: outboundScreeningNodeData,

  [FlowNodeTypes.ai_action]: aiActionNodeData,

  [FlowNodeTypes.initiate_call]: initiateCallNodeData,
  [FlowNodeTypes.note]: noteNodeData,

  [FlowNodeTypes.ai_assist]: aiAssistNodeData,

  [FlowNodeTypes.user_input]: userInputNodeData,

  [FlowNodeTypes.ai_agent_call]: aiAgentCallNodeData,

  [FlowNodeTypes.ai_agent_message]: aiAgentMessageNodeData,

  [FlowNodeTypes.ai_agent_chat]: aiAgentChatNodeData,

  [FlowNodeTypes.ai_agent_whatsapp]: aiAgentWhatsappNodeData,
  [FlowNodeTypes.branch_v2]: branchV2NodeData,
  [FlowNodeTypes.send_whatsapp]: sendWhatsappNodeData,

  [FlowNodeTypes.send_chat]: sendChatNodeData,

  [FlowNodeTypes.send_message]: sendMessageNodeData,
  [FlowNodeTypes.multi_party_call_outbound]: multiPartyCallOutboundNodeData,

  [FlowNodeTypes.whatsapp_conversation]: whatsappConversationNodeData,

  [FlowNodeTypes.message_conversation]: messageConversationNodeData,

  [FlowNodeTypes.business_hour]: businessHourNodeData,

  [FlowNodeTypes.app_action]: appActionNodeData,
  [FlowNodeTypes.http_request]: httpRequestNodeData,

  [FlowNodeTypes.create_task]: createTaskNodeData,
  [FlowNodeTypes.counter]: counterNodeData,
  [FlowNodeTypes.queue_and_route]: queueAndRouteNodeData,
  [FlowNodeTypes.customer_feedback]: customerFeedbackNodeData,
  [FlowNodeTypes.call_forward]: callForwardNodeData,
  [FlowNodeTypes.get_input]: getInputNodeData,
  [FlowNodeTypes.voicemail]: voicemailNodeData,
  [FlowNodeTypes.prompt]: promptNodeData,
  [FlowNodeTypes.menu]: getTempNodeData('Menu'),
  [FlowNodeTypes.select_trigger]: getTempNodeData('Select Trigger'),
  [FlowNodeTypes.ivr_menu]: defaultIVRNodeData,
  [FlowNodeTypes.hangup]: hangupNodeData,
  // [FlowNodeTypes.queue]: queueNodeData,
}

export const MenuNodeID = 'menu-node'
